import { Step } from "react-joyride";

/**
 * Guided tour steps for the Clarifications step (panel review mode).
 *
 * Each step targets a `data-testid` attribute rendered in the step.
 *
 * NOTE: react-joyride does NOT auto-skip a target that isn't mounted (an earlier
 * comment here claimed it did). It stalls, so a missing FIRST target makes "Show Me"
 * a dead button. `clarification-review-table` and `select-all-checkbox` only exist
 * when there is at least one clarification — with none, ClarificationReviewTable
 * early-returns `review-table-empty`. Build the steps for the state you are in.
 */
const reviewStepsWithClarifications: Step[] = [
	{
		target: '[data-testid="clarification-review-table"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Clarification Review</h3>
				<p>
					This table shows all clarification questions proposed by panel members.
					Click on a row to review the full details and decide whether to accept,
					decline, or mark as a duplicate.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="select-all-checkbox"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Batch Selection</h3>
				<p>
					Select multiple clarifications to perform batch actions — accept them
					all at once, decline with a reason, or mark a group as duplicates.
				</p>
			</div>
		),
		placement: "right",
		disableBeacon: true,
	},
	{
		target: '[data-testid="tab-accepted-clarifications"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Clarifications Register</h3>
				<p>
					Switch to this tab to view all accepted clarifications that will be
					sent to suppliers. You can review and edit them before finalising.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];

/**
 * The Lead arrives here with nothing to review — the common case when no panel
 * member raised a clarification during Initial Evaluation.
 */
const reviewStepsEmpty: Step[] = [
	{
		target: '[data-testid="review-table-empty"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Nothing to review</h3>
				<p>
					No clarification questions were proposed during Initial Evaluation, so
					there is nothing to accept or decline here.
				</p>
				<p className="mt-2 text-gray-500">
					You can move straight on to the next step.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="tab-accepted-clarifications"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Clarifications Register</h3>
				<p>
					The register lists every accepted clarification that will be sent to
					suppliers. It is empty until clarifications are accepted here.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
];

export interface ClarificationsGuideOptions {
	/** At least one clarification is loaded — the review table is mounted. */
	hasClarifications: boolean;
	/** The viewer can act on clarifications (the batch checkbox is Lead-only). */
	canManage: boolean;
}

export const buildClarificationsReviewSteps = ({
	hasClarifications,
	canManage,
}: ClarificationsGuideOptions): Step[] => {
	if (!hasClarifications) {
		return reviewStepsEmpty;
	}
	return reviewStepsWithClarifications.filter(
		(step) =>
			canManage ||
			step.target !== '[data-testid="select-all-checkbox"]'
	);
};

/** Back-compat default: a populated table viewed by the Lead. */
export const clarificationsReviewSteps: Step[] = reviewStepsWithClarifications;
